<template>
    <div v-show="searchIndex?.length && searchKey?.length && searchHost?.length" class="search--container"
         :class="containerClasses" ref="search" @click.stop @open="hideWidget = false" @close="hideWidget = true">
        <a v-if="minimizeWidget"
           class="search--handle visual_menu--glass_effect textual_menu--glass_effect" :class="searchHandleClasses"
           href="#" :title="(hideWidget) ? 'Open Search' : 'Close Search'"
           @click.prevent="handleSearchHandleClick"></a>
        <div class="viewport">
            <text-input @input.native="submitAutocomplete" @search.native="submitSearch" v-model:input="searchTerm"
                        @focus="focusHandler" label="Search Menu Items" type="search" ref="input" required
                        wide></text-input>
        </div>
        <ul ref="autocomplete" class="autocomplete_results--container"
            :class="{'autocomplete_results--container-hide': !showResults || !autocompleteResults.length}"
            :style="{'max-height': autocompleteMaxHeight}">
            <li v-for="(result, index) in autocompleteResults" :key="index"
                @click="handleResultClick(result)">
                <div class="autocomplete_results--content">
                    <h4 v-if="result.hasOwnProperty('_formatted') && result._formatted.hasOwnProperty('mi_title')"
                        v-html="result._formatted.mi_title"></h4>
                    <h4 v-else-if="result.mi_title.length">{{ result.mi_title }}</h4>
                    <h5 v-if="result.hasOwnProperty('_formatted') && result._formatted.hasOwnProperty('mi_subtitle')"
                        v-html="result._formatted.mi_subtitle"></h5>
                    <h5 v-else-if="result.mi_subtitle.length">{{ result.mi_subtitle }}</h5>
                    <p v-if="result.hasOwnProperty('_formatted') && result._formatted.hasOwnProperty('mi_description')"
                       v-html="result._formatted.mi_description"></p>
                    <p v-else-if="result.mi_description.length">{{ result.mi_description }}</p>
                </div>
                <a class="item_button--container item_button--container-view material-icons"
                   @click.prevent.stop="$emit('view-menu-item', result.mi_id);showResults = false;hideWidget = true"
                   href="#"></a>
                <a v-if="$store.state.selectedMenuItemIds.length"
                   class="item_button--container item_button--container-select material-icons"
                   @click.prevent.stop="$store.commit('toggleMenuItemSelected', [result.mi_id, menu?.menu_id || 0])" href="#"
                   :class="{'item_button--container-active': -1 !== $store.state.selectedMenuItemIds.indexOf(result.mi_id)}"
                   :title="(-1 !== $store.state.selectedMenuItemIds.indexOf(result.mi_id)) ? 'Remove favorite item' : 'Add favorite item'"></a>
            </li>
            <li v-if="!autocompleteResults.length"><i>No Results Found</i></li>
        </ul>
    </div>
</template>

<script lang="ts">
/**
 * Search widget for menus.
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.3.3
 * @copyright  Copyright (C) 2021-2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2021-12-30 (1.0.0) (jhoward)
 *    - Refactored from textual-menu.vue to be used with visual-menu.vue as well.
 *
 *  2022-01-11 (1.1.0) (jhoward)
 *    - Support fixed positioning.
 *
 *  2022-03-07 (1.1.1) (jhoward)
 *    - Do not show menu search results if search input is blank.
 *
 *  2022-05-11 (1.2.0) (jhoward)
 *    - Update minimized layout.
 *
 *  2022-05-24 (1.2.1) (jhoward)
 *    - <search-menu> now uses .visual_menu--glass_effect for stylization.
 *    - <search-menu> refactored handle click logic to handleSearchHandleClick() method.
 *
 *  2023-04-21 (1.3.0) (jhoward)
 *    - <search-menu> now uses TypeScript.
 *
 *  2023-08-03 (1.3.1) (jhoward)
 *    - Erase search term after submitting search or closing the search widget in <search-menu>.
 *
 *  2023-04-21 (1.3.2) (jhoward)
 *    - Add `open` and `close` event listeners on <search-menu> container element.
 *    - Coding style improvements in <search-menu>.
 *
 *  2023-08-26 (1.3.3) (jhoward)
 *    - Use store for selected items in <search-menu>.
 */

import {ComponentPublicInstance, defineComponent, PropType} from "vue";
import TextInput from '../material-form/text-input.vue';
import {Menu} from "./defs";
import {TextInputComponent} from "../material-form/defs";

export interface SearchMenuComponent extends ComponentPublicInstance {
  reset: () => void;
}

interface AutocompleteResult {
  menu: string;
  menu_id: number;
  mi_highlight: number;
  mi_item_group_name: string;
  mi_item_subgroup_name: string;
  mi_price: string;
  mi_id: number;
  mi_title: string;
  mi_subtitle: string;
  mi_description: string;
  ms_id: number;
  mt_title: string;
  section: string;
  tags: string[];
  _formatted: {
    menu: string;
    menu_id: number;
    mi_id: number;
    mi_item_group_name: string;
    mi_item_subgroup_name: string;
    mi_highlight: number;
    mi_price: string;
    mi_title: string;
    mi_subtitle: string;
    mi_description: string;
    ms_id: number;
    mt_title: string;
    section: string;
    tags: string[];
  };
}

interface AutocompleteResponse {
  exhaustiveNbHits: boolean;
  hits?: AutocompleteResult[];
  limit: number;
  nbHits: number;
  offset: number;
  processingTimeMs: number;
  query: string;
}

export default defineComponent({
  name: "search-menu",
  components: {
    TextInput,
  },
  props: {
    searchIndex: {
      type: String,
      default: ''
    },
    searchKey: {
      type: String,
      default: ''
    },
    searchHost: {
      type: String,
      default: ''
    },
    menu: {
      type: Object as PropType<Menu | null>,
      default: null
    },
    minimizeWidget: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchTerm: '',
      autocompleteResults: [] as AutocompleteResult[],
      showResults: false,
      hideWidget: this.minimizeWidget,
      observer: null as IntersectionObserver | null,
      isStickyStuck: false,
      isFixedPosition: false,
      windowScrollY: 0,
      windowHeight: window.innerHeight,
      autocompleteMaxHeightRender: 0,

      abortController: null as AbortController | null,
      abortSignal: null as AbortSignal | null,
    };
  },
  mounted() {
    this.isFixedPosition = (window.getComputedStyle(this.$el).position === 'fixed');

    this.setupIntersectionObserver();

    document.addEventListener('click', () => {
      this.showResults = false;
      if (this.minimizeWidget) {
        this.hideWidget = true;
      }
    });

    window.addEventListener('scroll', () => {
      this.windowScrollY = window.scrollY;
    }, {passive: true});

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
  },
  watch: {
    autocompleteResults() {
      this.autocompleteMaxHeightRender++;
    },
    hideWidget() {
      if (this.hideWidget) {
        this.reset();
      }
    }
  },
  computed: {

    autocompleteMaxHeight() {
      this.windowScrollY;
      this.autocompleteMaxHeightRender;
      let maxHeight = this.windowHeight;

      if (this.$refs.autocomplete) {
        maxHeight -= (this.$refs.autocomplete as HTMLLIElement).getBoundingClientRect().top;
      }

      if (this.$parent?.$el) {
        const sectionNavNode = this.$parent.$el.querySelector('.body_container--section_nav');
        if (sectionNavNode) {
          maxHeight -= sectionNavNode.clientHeight;
        }
      }

      return maxHeight + 'px';
    },

    containerClasses() {
      return {
        'search--container-minimize': this.minimizeWidget,
        'search--container-open': this.minimizeWidget && !this.hideWidget,
        'sticky-stuck': this.isStickyStuck || this.isFixedPosition
      };
    },

    searchHandleClasses() {
      return {
        'material-icons': !this.minimizeWidget || this.hideWidget
      };
    }

  },
  methods: {

    reset() {
      if (this.$refs?.input) {
        (this.$refs.input as TextInputComponent).currentValue = '';
      }
      this.autocompleteResults = [];
      this.showResults = false;
    },

    checkStickyStuck(entries: IntersectionObserverEntry[]) {
      this.isStickyStuck = (entries[0].intersectionRatio < 1);
    },

    setupIntersectionObserver() {
      if (window?.IntersectionObserver && !this.isFixedPosition) {
        //only need to set up the observer once
        if (!this.observer) {
          this.observer = new IntersectionObserver(this.checkStickyStuck, {
            threshold: [1]
          });
          if (this.$el.previousElementSibling) {
            this.observer.observe(this.$el.previousElementSibling);
          } else {
            this.observer.observe(this.$el);
          }
        }
      }
    },

    focusHandler() {
      if (this.searchTerm !== '') {
        this.showResults = true;
      }
    },

    submitAutocomplete() {
      if (this.searchTerm === '') {
        this.showResults = false;
      } else {

        if (this.abortSignal && this.abortController) {
          this.abortController.abort();
        }

        if (window?.AbortController) {
          this.abortController = new AbortController();
          this.abortSignal = this.abortController.signal;
        }

        fetch(this.searchHost + '/indexes/' + this.searchIndex + '/search', {
          method: 'POST',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
            'X-Meili-API-Key': this.searchKey
          },
          redirect: 'follow',
          body: JSON.stringify({
            q: this.searchTerm,
            filter: 'menu_id=' + this.menu?.menu_id || 0,
            cropLength: 50,
            attributesToCrop: ['mi_title', 'mi_subtitle', 'mi_description'],
            attributesToHighlight: ['mi_title', 'mi_subtitle', 'mi_description'],
          }),
          signal: this.abortSignal
        })
          .then(response => {
            return response.json();
          })
          .then((json: AutocompleteResponse) => {
            if (json?.hits) {
              this.autocompleteResults = json.hits;
              this.showResults = true;
            } else {
              this.autocompleteResults = [];
              this.showResults = false;
            }
          })
          .catch(e => {
            console.log(e);
          })
          .finally(() => {
            this.abortController = null;
            this.abortSignal = null;
          })
      }
    },

    submitSearch() {
      if (!this.searchTerm.length) {
        this.reset();
      } else {
        //TODO: filter down to selected results?
      }
    },

    handleSearchHandleClick() {
      this.hideWidget = !this.hideWidget;

      if (!this.hideWidget) {
        (this.$refs.input as TextInputComponent | null)?.$el.querySelector('input')?.focus();
      } else {
        this.showResults = false;
      }
    },

    handleResultClick(result: AutocompleteResult) {
      this.$emit('view-menu-item', result.mi_id);
      this.showResults = false;
      this.hideWidget = true;
      this.reset();
    }

  }
})
</script>

<style scoped>
.search--container {
  position: sticky;
  top: calc(3.2rem - 2px);
  z-index: 30;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  background-color: #ecefed;
  font-size: 1rem;
}

.menu_switcher--container-collapsable ~ .search--container {
  z-index: 10;
}

.menu_switcher--container-collapsable ~ .search--container-open {
  z-index: 20;
}

.search--container-minimize {
  position: fixed;
  top: 0;
  right: 0;
  transform: translateY(-100%);
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.search--container-minimize.search--container-open {
  transform: none;
}

.search--container .viewport {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  background-color: #ecefed;
  z-index: 5;
}

.search--container-open .viewport {
  padding-right: 9em !important;
}

.search--container .search--handle {
  position: absolute;
  top: 100%;
  right: 0;
  font-size: 3em;
  text-decoration: none;
  border-radius: 0.25em;
  /*box-shadow: 0 0 1em 0.5em rgb(236 239 237), inset 0 0 1em 0.5em rgb(236 239 237);*/
  z-index: 7;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.search--container-open .search--handle {
  transform: translateY(-1.8em);
  padding-left: 0.5em;
  padding-right: 0.5em;
  font-size: 2.35em;
}

.search--handle::before {
  content: 'search';
}

.search--container-open .search--handle::before {
  content: 'Close';
}

.autocomplete_results--container {
  list-style: none;
  font-size: 0.8rem;
  overflow: hidden;
  overflow-y: auto;
  padding: 0;
  position: absolute;
  background-color: #ecefed;
  width: 100%;
}

.autocomplete_results--container-hide {
  opacity: 0;
  visibility: hidden;
  max-height: 0;
  overflow: hidden;
}

.autocomplete_results--container h5,
.autocomplete_results--container {
  margin-bottom: 0;
}

.autocomplete_results--container em {
  font-weight: bold;
  color: #007966;
}

.autocomplete_results--content {
  grid-row: 1 / -1;
  grid-column: 1;
}

.autocomplete_results--container li {
  padding: 12px 32px;
  display: grid;
  grid-template-columns: 1fr 32px;
  grid-template-rows: 1fr 1fr;
  align-items: center;
  grid-gap: 8px;
}

.autocomplete_results--container li:nth-child(odd) {
  background: #FFF;
}


.body-container.search-bar {
  padding-bottom: 0;
  padding-top: 0;

  /*disabled for now*/
  display: none;
}

.search--container.sticky-stuck,
.search--container.sticky-stuck .autocomplete_results--container {
  box-shadow: 0 -2px 6px 4px rgba(0, 0, 0, 0.54);
}

.search--container.sticky-stuck.search--container-minimize {
  box-shadow: none;
}

.search--container.sticky-stuck.search--container-open {
  box-shadow: inherit;
}


.item_button--container {
  position: relative;
  color: rgba(0, 0, 0, 0.54);
  width: 24px;
  justify-self: end;
  text-decoration: none;
}

.item_button--container-active {
  color: rgba(0, 103, 87, 0.54);
}

.item_button--container-view::before {
  content: 'visibility';
}


/*TABLET + MOBILE*/
@media screen and (max-width: 1024px) {
  .search--container .viewport {
    padding-left: 32px;
    padding-right: 32px;
  }
}
</style>